/* =============================================================
   landingFx.js — lightweight scroll / motion primitives for the
   landing page. No external deps (framer-motion etc. are not
   installed), so everything is hand-rolled on rAF + Intersection
   Observer and fully respects prefers-reduced-motion.
   ============================================================= */
import React, { useEffect, useRef, useState, useCallback } from 'react';

/* Detect reduced-motion once (and react to changes). */
export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    // Safari <14 uses addListener
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);
  return reduced;
};

/* -------------------------------------------------------------
   Reveal — fades + slides children in once they scroll into view.
   Adds .reveal / .is-visible classes that LandingPage.css styles.
   ------------------------------------------------------------- */
export const Reveal = ({
  as: Tag = 'div',
  className = '',
  children,
  delay = 0,
  y = 28,
  once = true,
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) {
      setVisible(true);
      return undefined;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced, once]);

  const style = {
    transitionDelay: visible ? `${delay}ms` : '0ms',
    '--reveal-y': `${y}px`,
    ...(rest.style || {}),
  };

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}
      {...rest}
      style={style}
    >
      {children}
    </Tag>
  );
};

/* -------------------------------------------------------------
   useParallax — returns a ref; on scroll it sets a CSS var
   (--parallax) you can consume with translate3d. `speed` is a
   fraction of scroll distance (negative moves up faster).
   ------------------------------------------------------------- */
export const useParallax = (speed = 0.15) => {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    let frame = null;

    const apply = () => {
      frame = null;
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      // distance of element center from viewport center, normalized
      const fromCenter = rect.top + rect.height / 2 - viewport / 2;
      const offset = -fromCenter * speed;
      el.style.setProperty('--parallax', `${offset.toFixed(1)}px`);
    };

    const onScroll = () => {
      if (frame == null) frame = window.requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [speed, reduced]);

  return ref;
};

/* -------------------------------------------------------------
   useMouseTilt — subtle 3D tilt on pointer move for hero device.
   Sets --rx / --ry CSS vars (degrees). Disabled on touch + RM.
   ------------------------------------------------------------- */
export const useMouseTilt = (max = 8) => {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (reduced) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia && window.matchMedia('(hover: none)').matches) return undefined;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.setProperty('--ry', `${(px * max).toFixed(2)}deg`);
      el.style.setProperty('--rx', `${(-py * max).toFixed(2)}deg`);
    };
    const reset = () => {
      el.style.setProperty('--ry', '0deg');
      el.style.setProperty('--rx', '0deg');
    };

    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', reset);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', reset);
    };
  }, [max, reduced]);

  return ref;
};

/* -------------------------------------------------------------
   CountUp — animates a number from 0 → value when scrolled into
   view. Formats with separators and optional prefix/suffix.
   ------------------------------------------------------------- */
export const CountUp = ({
  value = 0,
  duration = 1600,
  prefix = '',
  suffix = '',
  decimals = 0,
  className = '',
}) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const reduced = usePrefersReducedMotion();

  const format = useCallback(
    (n) => {
      const fixed = Number(n).toFixed(decimals);
      const [int, dec] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return `${prefix}${grouped}${dec ? `.${dec}` : ''}${suffix}`;
    },
    [decimals, prefix, suffix]
  );

  useEffect(() => {
    if (reduced) {
      setDisplay(value);
      return undefined;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setDisplay(value);
      return undefined;
    }
    let raf = null;
    let started = false;

    const run = (ts0) => {
      const tick = (ts) => {
        const elapsed = ts - ts0;
        const p = Math.min(elapsed / duration, 1);
        // easeOutExpo
        const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        setDisplay(value * eased);
        if (p < 1) raf = window.requestAnimationFrame(tick);
      };
      raf = window.requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            window.requestAnimationFrame((ts) => run(ts));
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [value, duration, reduced]);

  return (
    <span ref={ref} className={className}>
      {format(display)}
    </span>
  );
};

/* -------------------------------------------------------------
   useScrollProgress — 0→1 progress of page scroll, throttled to
   rAF. Used to drive the top neon progress bar.
   ------------------------------------------------------------- */
export const useScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let frame = null;
    const apply = () => {
      frame = null;
      const doc = document.documentElement;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      setProgress(Math.min(Math.max(window.scrollY / max, 0), 1));
    };
    const onScroll = () => {
      if (frame == null) frame = window.requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
  return progress;
};
